import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Search, Check } from 'lucide-react';
import colors from 'theme/colors';
import menuApi from 'api/requests/menu';
import promotionsApi from 'api/requests/promotions';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

export default function StepProducts({ data, onChange, slug, editingId }) {
    const [products, setProducts] = useState([]);
    const [inUseIds, setInUseIds] = useState(new Set());
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');

    function set(key, value) {
        onChange({ ...data, [key]: value });
    }

    useEffect(() => {
        if (!slug) return;

        async function load() {
            setLoading(true);
            try {
                const [prodRes, promoRes] = await Promise.all([
                    menuApi.getProducts(slug),
                    promotionsApi.list(slug),
                ]);
                const allProducts = prodRes.data.data || [];
                const allPromotions = promoRes.data.data || promoRes.data || [];

                const used = new Set();
                allPromotions.forEach(promo => {
                    if (promo.id === editingId) return;
                    (promo.products || []).forEach(p => used.add(p.id));
                });

                setProducts(allProducts);
                setInUseIds(used);
            } catch {}
            finally { setLoading(false); }
        }

        load();
    }, [slug, editingId]);

    function toggleProduct(id) {
        const current = data.product_ids || [];
        const next = current.includes(id)
            ? current.filter(i => i !== id)
            : [...current, id];
        set('product_ids', next);
    }

    const filtered = products.filter(p =>
        !search.trim() || p.name.toLowerCase().includes(search.toLowerCase())
    );

    const selected = new Set(data.product_ids || []);

    if (loading) {
        return (
            <LoadingWrap>
                <Spinner />
                <LoadingText>Carregando produtos...</LoadingText>
            </LoadingWrap>
        );
    }

    return (
        <Container>
            <SearchBar>
                <Search size={15} color='#999' />
                <SearchInput
                    type='text'
                    placeholder='Buscar produtos...'
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    autoFocus
                />
            </SearchBar>

            <SelectedCount>
                {selected.size} {selected.size === 1 ? 'produto selecionado' : 'produtos selecionados'}
            </SelectedCount>

            <ProductList>
                {filtered.length === 0 && (
                    <EmptyMsg>Nenhum produto encontrado</EmptyMsg>
                )}
                {filtered.map(p => {
                    const isSelected = selected.has(p.id);
                    const isDisabled = inUseIds.has(p.id);
                    return (
                        <ProductRow
                            key={p.id}
                            type='button'
                            $selected={isSelected}
                            $disabled={isDisabled}
                            onClick={() => !isDisabled && toggleProduct(p.id)}
                            title={isDisabled ? 'Já em uso em outra promoção' : undefined}
                            disabled={isDisabled}
                        >
                            <CheckBox $selected={isSelected} $disabled={isDisabled}>
                                {isSelected && <Check size={12} color='#fff' strokeWidth={3} />}
                            </CheckBox>
                            <ProductInfo>
                                <ProductName $disabled={isDisabled}>{p.name}</ProductName>
                                {p.base_price != null && (
                                    <ProductPrice>
                                        {Number(p.base_price).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                    </ProductPrice>
                                )}
                            </ProductInfo>
                            {isDisabled && <InUseBadge>Em uso</InUseBadge>}
                        </ProductRow>
                    );
                })}
            </ProductList>
        </Container>
    );
}

const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;

const Container = styled.div`display: flex; flex-direction: column; gap: 12px; flex: 1; min-height: 0;`;

const SearchBar = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    border: 1.5px solid ${colors.neutralDark};
    border-radius: 10px;
    padding: 0 12px;
    background: #fff;
    transition: border-color 0.15s;
    &:focus-within { border-color: ${panelAccent(colors.occasionalPurple)}; }
`;

const SearchInput = styled.input`
    flex: 1;
    border: none;
    padding: 11px 0;
    font-size: 14px;
    outline: none;
    color: ${colors.nightDark};
    background: transparent;
    &::placeholder { color: #999; }
`;

const SelectedCount = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: ${panelAccent(colors.occasionalPurple)};
`;

const ProductList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    &::-webkit-scrollbar { width: 3px; }
    &::-webkit-scrollbar-thumb { background: ${colors.neutralDark}; border-radius: 10px; }
`;

const ProductRow = styled.button`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    border-radius: 10px;
    border: 1.5px solid ${({ $selected, $disabled }) =>
        $disabled ? colors.neutralDark :
        $selected ? panelAccent(colors.occasionalPurple) : colors.neutralDark};
    background: ${({ $selected, $disabled }) =>
        $disabled ? colors.neutralDark :
        $selected ? `${panelAccentAlpha(colors.occasionalPurple, '0A')}` : '#fff'};
    cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};
    opacity: ${({ $disabled }) => $disabled ? 0.55 : 1};
    text-align: left;
    transition: all 0.15s;
    &:hover:not(:disabled) {
        border-color: ${panelAccentAlpha(colors.occasionalPurple, '60')};
    }
`;

const CheckBox = styled.div`
    width: 20px;
    height: 20px;
    border-radius: 6px;
    border: 1.5px solid ${({ $selected, $disabled }) =>
        $disabled ? colors.neutralDark :
        $selected ? panelAccent(colors.occasionalPurple) : '#ccc'};
    background: ${({ $selected }) => $selected ? panelAccent(colors.occasionalPurple) : 'transparent'};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.15s;
`;

const ProductInfo = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const ProductName = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${({ $disabled }) => $disabled ? colors.nightLight : colors.nightDark};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ProductPrice = styled.span`
    font-size: 12px;
    color: ${colors.nightLight};
`;

const InUseBadge = styled.span`
    font-size: 11px;
    font-weight: 600;
    color: ${colors.nightLight};
    background: ${colors.neutralDark};
    padding: 3px 8px;
    border-radius: 6px;
    flex-shrink: 0;
`;

const EmptyMsg = styled.p`
    text-align: center;
    font-size: 14px;
    color: ${colors.nightLight};
    padding: 24px 0;
    margin: 0;
`;

const LoadingWrap = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 48px 0;
`;

const Spinner = styled.div`
    width: 28px; height: 28px;
    border: 3px solid ${colors.neutralDark};
    border-top-color: ${panelAccent(colors.occasionalPurple)};
    border-radius: 50%;
    animation: ${spin} 0.7s linear infinite;
`;

const LoadingText = styled.span`font-size: 13px; color: ${colors.nightLight};`;
